import React, { memo, useContext, useMemo } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import queryString from 'query-string';
import isEmpty from 'lodash/isEmpty';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faPlay,
  faSyncAlt,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';

import CurrentOrgContext from '../../Context/CurrentOrgContext';
import IconsContext from '../../Context/IconsContext';
import OrgsMapContext from '../../Context/OrgsMapContext';
import AssetUtils from '../../Lib/AssetUtils';
import { hasReadAccessToResource } from '../../RBAC/AccessTools';

import AssetTag from './AssetTag';
import Can from '../../RBAC/Can';
import {
  ASSET_MODEL,
  ASSET_MODEL_READ,
  GATEWAY_VERSION_UPDATE,
  REMOTE,
} from '../../Constants/RBAC';

export default memo(AssetDetailsTabDisplay);
function AssetDetailsTabDisplay(props) {
  const {
    activeSignals,
    asset,
    assetAddress,
    assetCoords,
    assetGroups,
    assetIsOnline,
    bridgeElevatURL,
    bridgeKey,
    bridgeKeyUpdatedTime,
    dateActivated,
    dateCreated,
    deviceStreamsElevatURL,
    deviceStreamsError,
    haveSystemAdminData,
    identityAssetIsOnline,
    identityConnectionStateUpdatedTime,
    identityLastActivityTime,
    identity2AssetIsOnline,
    identity2ConnectionStateUpdatedTime,
    identity2LastActivityTime,
    isRestrictedAssetForOtherThings,
    otaHiddenForEdge,
    lastUpdated,
    onlineTime,
    packagesToUpdate,
    toggleModal,
    toggleRawAPIModal,
    toggleUploadCustomerOTAModal,
    toggleRunCustomerOTAModal,
    toggleDeleteCustomerOTAModal,
    updateInProgress,
    customerOTAlist,
  } = props;

  const currentOrg = useContext(CurrentOrgContext);
  const { cumminsIconBlack } = useContext(IconsContext);
  const orgsMap = useContext(OrgsMapContext);
  const assetLinks = asset?.gtemplate?.links ?? [];

  const assetUtils = useMemo(() => new AssetUtils(), []);
  const orgUsesNewBilling = useMemo(
    () => currentOrg?.features?.billing?.enabled,
    [currentOrg?.features?.billing?.enabled]
  );

  return (
    <div className="flex-grow-1 row nine-tenth-text">
      <div className="col-12 col-md-6 col-lg-4 px-0 px-md-4 mb-3 mb-lg-0 text-text-dark d-lg-flex flex-lg-column">
        <div className="border rounded p-3">
          <h6 className="font-weight-bold mb-3">Info</h6>
          <div className="d-flex justify-content-between mt-lg-2">
            <div className="text-text-light-gray">Asset Model</div>
            <div className="font-weight-bold text-text-dark text-right">
              <Can
                no={() => (
                  <button
                    className="btn btn-link p-0 m-0 border-0 nine-tenth-text"
                    disabled
                  >
                    {asset?.gtemplate?.name ?? '--'}
                  </button>
                )}
                orgID={asset?.orgID}
                perform={ASSET_MODEL_READ}
                resource={ASSET_MODEL}
                sharedGtemplate={asset?.gtemplate?.sharedGtemplate}
                yes={() => (
                  <Link
                    className="btn btn-link p-0 m-0 border-0 nine-tenth-text"
                    to={`/asset-model/${asset?.gtemplateID ?? ''}`}
                  >
                    {asset?.gtemplate?.name ?? '--'}
                  </Link>
                )}
              />
            </div>
          </div>
          <hr className="d-md-none" />
          <div className="d-flex justify-content-between mt-lg-3">
            <div className="text-text-light-gray">Gateway</div>
            <div className="font-weight-bold text-text-dark text-right">
              {asset?.nickname ?? '--'}
            </div>
          </div>
          <hr className="d-md-none" />
          <div className="d-flex justify-content-between mt-lg-3">
            <div className="text-text-light-gray">Location</div>
            <div className="font-weight-bold text-text-dark text-right">
              {assetAddress ? assetAddress : '--'}
            </div>
          </div>
          <hr className="d-md-none" />
          <div className="d-flex justify-content-between mt-lg-3">
            <div className="text-text-light-gray">Timezone</div>
            <div className="font-weight-bold text-text-dark text-right">
              {asset?.timezone ?? '--'}
            </div>
          </div>
          <hr className="d-md-none" />
          <div className="d-flex justify-content-between mt-lg-3">
            <div className="text-text-light-gray">Owner</div>
            <div className="font-weight-bold text-text-dark text-right">
              {orgsMap[asset?.orgID]?.name ?? '--'}
            </div>
          </div>
          {asset?.gtemplate?.settings?.engineanalysis ? (
            <>
              <hr className="d-md-none" />
              <div className="d-flex justify-content-between mt-lg-3">
                <div className="d-flex align-items-center">
                  <img
                    alt="cummins logo"
                    className="mr-1"
                    height={25}
                    src={cumminsIconBlack}
                    width={25}
                  />
                  <p className="mb-0 text-text-light-gray">Diagnostics</p>
                </div>
                <Link
                  className="p-0 m-0 border-0 nine-tenth-text text-right"
                  to={`/engineanalysis/${asset?._id}?${queryString.stringify({
                    viewing: 'details',
                  })}`}
                >
                  View Details
                </Link>
              </div>
            </>
          ) : null}
          {isRestrictedAssetForOtherThings || otaHiddenForEdge ? null : (
            <>
              <hr className="d-md-none" />
              <Can
                orgID={asset?.orgID}
                perform={GATEWAY_VERSION_UPDATE}
                resource={REMOTE}
                yes={() => (
                  <div className="d-flex justify-content-between mt-lg-3">
                    <div className="text-text-light-gray">
                      Gateway Software Version
                    </div>
                    <div className="font-weight-bold text-text-dark text-right">
                      <button
                        className={`btn ${
                          isEmpty(packagesToUpdate)
                            ? 'text-alert-confirmation border-alert-confirmation'
                            : updateInProgress
                              ? 'text-gradient-first-blue border-gradient-first-blue'
                              : 'text-gradient-second-red border-gradient-second-red'
                        } rounded three-quarter-text py-0 mr-1`}
                        onClick={toggleModal}
                        disabled={isEmpty(packagesToUpdate) || updateInProgress}
                      >
                        {isEmpty(packagesToUpdate) ? (
                          'Up to date'
                        ) : updateInProgress ? (
                          <div>
                            <FontAwesomeIcon icon={faSyncAlt} size="xs" />
                            <span className="pl-2">In Progress</span>
                          </div>
                        ) : (
                          'Update Available'
                        )}
                      </button>
                    </div>
                    <div className="font-weight-bold text-text-dark">
                      <button
                        className="p-1 btn btn-sm modal-button-primary three-quarter-text"
                        onClick={() => toggleRawAPIModal('OTAs')}
                      >
                        View OTA Data
                      </button>
                    </div>
                  </div>
                )}
              />
            </>
          )}
          {hasReadAccessToResource(asset?.orgID, REMOTE) &&
          !isRestrictedAssetForOtherThings ? (
            <>
              <hr className="d-md-none" />
              <div className="d-flex justify-content-between mt-lg-2">
                <div className="text-text-light-gray">Main IoT Hub</div>
                <div className="font-weight-bold text-text-dark text-right">
                  <button
                    className="p-1 btn btn-sm modal-button-primary three-quarter-text"
                    onClick={() => toggleRawAPIModal('twin')}
                  >
                    View Twin
                  </button>
                </div>
              </div>
              <hr className="d-md-none" />
              <div className="d-flex justify-content-between mt-lg-2">
                <div className="text-text-light-gray">Alt IoT Hub</div>
                <div className="font-weight-bold text-text-dark text-right">
                  <button
                    className="p-1 btn btn-sm modal-button-primary three-quarter-text"
                    onClick={() => toggleRawAPIModal('twin2')}
                  >
                    View Twin
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
        {haveSystemAdminData && !isRestrictedAssetForOtherThings ? (
          <div className="border rounded mt-3 flex-fill p-3">
            <h6 className="font-weight-bold">Remote Access</h6>
            <div className="d-flex justify-content-between mt-lg-2">
              <div className="text-text-light-gray">Bridge Link</div>
              <div className="font-weight-bold text-text-dark text-right">
                <a href={bridgeElevatURL} className="btn btn-link p-0">
                  Click to Open Windows Client
                </a>
              </div>
            </div>
            <hr className="d-md-none" />
            <div className="d-flex justify-content-between mt-lg-2">
              <div className="text-text-light-gray">Bridge Key</div>
              <div className="font-weight-bold text-text-dark">
                {bridgeKey || '--'}
              </div>
            </div>
            <div className="d-flex justify-content-between mt-lg-2">
              <div className="text-text-light-gray">Bridge Key Updated</div>
              <div className="font-weight-bold text-text-dark text-right">
                {bridgeKeyUpdatedTime || '--'}
              </div>
            </div>
            <hr className="d-md-none" />
            <div className="d-flex justify-content-between mt-lg-2">
              <div className="text-text-light-gray">Elevat Device Link</div>
              <div className="font-weight-bold text-text-dark text-right">
                {deviceStreamsError ? (
                  <div
                    className="text-text-light-gray"
                    data-for="deviceStreamsError"
                    data-tip={deviceStreamsError}
                  >
                    not available
                    <ReactTooltip id="deviceStreamsError" />
                  </div>
                ) : (
                  <a href={deviceStreamsElevatURL} className="btn btn-link p-0">
                    Click to Open Windows Client
                  </a>
                )}
              </div>
            </div>
            <hr className="d-md-none" />
            <div className="d-flex justify-content-between mt-lg-2">
              <div className="text-text-light-gray">
                Device Link IoT Hub State
              </div>
              <div className="d-flex align-items-center justify-content-end">
                <FontAwesomeIcon
                  icon={faCircle}
                  size="xs"
                  className={`text-${
                    identity2AssetIsOnline ? 'alert-confirmation' : 'hr-gray'
                  }`}
                />
                <span
                  className={`${
                    identity2AssetIsOnline
                      ? 'text-success'
                      : 'text-icon-link-gray'
                  } ml-1`}
                >
                  {identity2AssetIsOnline ? 'Online' : 'Offline'}
                </span>
                <span className="ml-1 font-weight-bold text-text-dark text-right">
                  {`updated ${identity2ConnectionStateUpdatedTime}`}
                </span>
              </div>
            </div>
            <hr className="d-md-none" />
            <div className="d-flex justify-content-between mt-lg-2">
              <div className="text-text-light-gray">
                Device Link IoT Hub Last Activity
              </div>
              <div className="font-weight-bold text-text-dark text-right">
                {identity2LastActivityTime || '--'}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <div className="col-12 col-md-6 col-lg-4 px-0 px-md-4 mb-3 mb-lg-0 text-text-dark d-md-flex flex-md-column">
        <div className="border rounded p-3">
          <h6 className="font-weight-bold mb-3">Stats</h6>
          <div className="d-flex align-items-center">
            <FontAwesomeIcon
              icon={faCircle}
              size="xs"
              className={`text-${
                assetIsOnline ? 'alert-confirmation' : 'hr-gray'
              }`}
            />
            <span
              className={`${
                assetIsOnline ? 'text-success' : 'text-icon-link-gray'
              } ml-1`}
            >
              {assetIsOnline ? 'Online' : 'Offline'}
            </span>
            <span
              className={
                assetIsOnline
                  ? 'text-gray font-weight-light ml-1 three-quarter-text'
                  : 'd-none'
              }
            >
              {`for ${onlineTime}`}
            </span>
          </div>
          <div className="d-flex justify-content-between mt-lg-2">
            <div className="text-text-light-gray">Last Updated</div>
            <div className="font-weight-bold text-text-dark text-right">
              {lastUpdated ? lastUpdated : '--'}
            </div>
          </div>
          {haveSystemAdminData && !isRestrictedAssetForOtherThings ? (
            <>
              <hr className="d-md-none" />
              <div className="d-flex justify-content-between mt-lg-2">
                <div className="text-text-light-gray">IoT Hub State</div>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon
                    icon={faCircle}
                    size="xs"
                    className={`text-${
                      identityAssetIsOnline ? 'alert-confirmation' : 'hr-gray'
                    }`}
                  />
                  <span
                    className={`${
                      identityAssetIsOnline
                        ? 'text-success'
                        : 'text-icon-link-gray'
                    } ml-1`}
                  >
                    {identityAssetIsOnline ? 'Online' : 'Offline'}
                  </span>
                  <span className="ml-1 font-weight-bold text-text-dark text-right">
                    {`updated ${identityConnectionStateUpdatedTime}`}
                  </span>
                </div>
              </div>
              <hr className="d-md-none" />
              <div className="d-flex justify-content-between mt-lg-2">
                <div className="text-text-light-gray">
                  IoT Hub Last Activity
                </div>
                <div className="font-weight-bold text-text-dark text-right">
                  {identityLastActivityTime ? identityLastActivityTime : '--'}
                </div>
              </div>
            </>
          ) : null}
          <hr className="d-md-none" />
          <div className="d-flex justify-content-between mt-lg-2">
            <div className="text-text-light-gray">Serial Number</div>
            <div className="font-weight-bold text-text-dark text-right">
              {asset?.mac ?? '--'}
            </div>
          </div>
          <hr className="d-md-none" />
          <div className="d-flex justify-content-between mt-lg-2">
            <div className="text-text-light-gray">Coordinates</div>
            <div className="font-weight-bold text-text-dark text-right">
              {assetCoords ? assetCoords : '--'}
            </div>
          </div>
          <hr className="d-md-none" />
          <div className="d-flex justify-content-between mt-lg-2">
            <div className="text-text-light-gray">Signals Reporting Data</div>
            <div className="font-weight-bold text-text-dark text-right">
              {activeSignals ? activeSignals : '--'}
            </div>
          </div>
          <hr className="d-md-none" />
          <div className="d-flex justify-content-between mt-lg-2">
            <div className="text-text-light-gray">Date Created</div>
            <div className="font-weight-bold text-text-dark text-right">
              {dateCreated ? dateCreated : '--'}
            </div>
          </div>
          <hr className="d-md-none" />
          {orgUsesNewBilling ? (
            <>
              <div className="d-flex justify-content-between mt-lg-2">
                <div className="text-text-light-gray">
                  Subscription Start Date
                </div>
                <div className="font-weight-bold text-text-dark text-right">
                  {assetUtils.parseDateToYearMonthDay(
                    asset?.assetAssociation?.term_start
                  )}
                </div>
              </div>
              <hr className="d-md-none" />
              <div className="d-flex justify-content-between mt-lg-2">
                <div className="text-text-light-gray">
                  Subscription Renewal Date
                </div>
                <div className="font-weight-bold text-text-dark text-right">
                  {assetUtils.parseDateToYearMonthDay(
                    asset?.assetAssociation?.renewal_date
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-between mt-lg-2">
              <div className="text-text-light-gray">Date Activated</div>
              <div className="font-weight-bold text-text-dark text-right">
                {dateActivated}
              </div>
            </div>
          )}
          <hr className="d-md-none" />
          <div className="d-flex justify-content-between mt-lg-2">
            <div className="text-text-light-gray">UUID</div>
            <div className="font-weight-bold three-quarter-text asset-uuid text-text-dark text-right">
              {asset?._id ?? '--'}
            </div>
          </div>
        </div>
        <div
          className={`border rounded p-3 mt-3 ${
            customerOTAlist?.length ? 'flex-md-fill' : ''
          } d-lg-flex flex-lg-column`}
        >
          <div className="w-100 mb-3 d-flex align-items-center justify-content-between">
            <h6 className="mb-0 font-weight-bold">Customer OTAs</h6>
            <button
              className="p-1 btn btn-sm modal-button-primary three-quarter-text"
              onClick={() => toggleUploadCustomerOTAModal()}
            >
              Upload new OTA file
            </button>
          </div>
          <div className="flex-fill position-relative">
            <div className="inner-otas-container">
              {customerOTAlist?.length
                ? customerOTAlist.map((customerOTAselected, i) => (
                    <div
                      className="row no-gutters align-items-center justify-content-between my-2"
                      key={i}
                    >
                      <div className="col-auto">
                        <button
                          className="btn btn-danger px-1 py-0"
                          data-for={`deleteOta-${i}`}
                          data-tip="Delete OTA"
                          data-type="error"
                          onClick={() =>
                            toggleDeleteCustomerOTAModal(customerOTAselected)
                          }
                        >
                          <FontAwesomeIcon icon={faTrashAlt} size="xs" />
                          <ReactTooltip id={`deleteOta-${i}`} />
                        </button>
                      </div>
                      <div
                        className="col px-3 d-flex align-items-center justify-content-center"
                        data-for={`otaDetails-${i}`}
                        data-multiline
                        data-tip={`Type: ${customerOTAselected.typeName}<br />Created: ${customerOTAselected.createdOn} ago`}
                        data-type="info"
                      >
                        <p className="font-weight-bold text-text-dark three-quarter-text text-center text-break mb-0">
                          {customerOTAselected.displayName}
                        </p>
                        <ReactTooltip id={`otaDetails-${i}`} />
                      </div>
                      <div className="col-auto">
                        <button
                          className="btn btn-sm modal-button-primary text-white px-1 py-0"
                          data-arrow-color="transparent"
                          data-class="bg-brand-primary text-white"
                          data-for={`runOta-${i}`}
                          data-tip="Run OTA"
                          onClick={() =>
                            toggleRunCustomerOTAModal(customerOTAselected)
                          }
                        >
                          <FontAwesomeIcon icon={faPlay} size="xs" />
                          <ReactTooltip id={`runOta-${i}`} />
                        </button>
                      </div>
                    </div>
                  ))
                : null}
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 col-md-6 col-lg-4 px-0 px-md-4 text-text-dark d-flex flex-column mb-3 mb-lg-0">
        {currentOrg?.features?.apps?.length ? (
          <div
            className="flex-fill border rounded p-3 mb-3"
            style={{ minHeight: '10rem' }}
          >
            <h6 className="font-weight-bold">Groups</h6>
            <div className="outer-groups-container">
              <div className="inner-groups-container w-100">
                {assetGroups?.map?.(({ _id, name, orgID, type }) => {
                  const urlBase = type === 'waterpump' ? 'watertransfer' : type;
                  return (
                    <Link
                      className="btn btn-link d-block text-left p-0 m-0 border-0 nine-tenth-text"
                      key={_id}
                      to={`/${urlBase}/${_id}?orgID=${orgID}&viewing=lineDetail`}
                    >
                      {name ?? '--'}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
        <div className="border rounded p-3" style={{ minHeight: '10rem' }}>
          <h6 className="font-weight-bold mb-3">Links</h6>
          {assetLinks.map(({ name, link }, i) => {
            return link ? (
              <div className="mt-1 mt-lg-2" key={i}>
                <a
                  href={link}
                  className="btn btn-link p-0"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {name || link}
                </a>
              </div>
            ) : null;
          })}
        </div>
        <div className="border rounded p-3 mt-3" style={{ minHeight: '10rem' }}>
          <h6 className="font-weight-bold">Tags</h6>
          {asset?.tags?.length ? (
            <div className="outer-tags-container">
              <div className="inner-tags-container w-100">
                {asset.tags.map((tag) => (
                  <AssetTag
                    key={tag._id}
                    tagName={tag.name}
                    tagValue={tag.value}
                    tagID={tag._id}
                    hideDeleteButton={true}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <style jsx>{`
        .row {
          margin-top: 0.5rem;
          margin-left: 0;
          margin-right: 0;
        }
        .dynamic-content-row {
          height: 1.5rem;
        }
        .profile-image-container {
          height: 8rem;
        }
        .asset-uuid {
          line-height: 2;
        }
        .text-gray {
          color: var(--text-light-gray);
        }
        .border {
          border-color: var(--lightest-gray) !important;
        }
        @media (min-width: 992px) {
          .outer-groups-container,
          .outer-tags-container {
            position: relative;
            height: 100%;
          }
          .inner-groups-container,
          .inner-tags-container {
            position: absolute;
            height: 80%;
            overflow: auto;
          }
          .inner-otas-container {
            height: 100%;
            overflow: auto;
            position: absolute;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

AssetDetailsTabDisplay.propTypes = {
  asset: PropTypes.object,
  assetAddress: PropTypes.string,
  assetGroups: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      orgID: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  assetIsOnline: PropTypes.bool,
  onlineTime: PropTypes.string,
  lastUpdated: PropTypes.string,
  assetCoords: PropTypes.string,
  activeSignals: PropTypes.number,
  dateCreated: PropTypes.string,
  dateActivated: PropTypes.string,
  toggleModal: PropTypes.func,
  packagesToUpdate: PropTypes.arrayOf(PropTypes.string),
  updateInProgress: PropTypes.bool,
  haveSystemAdminData: PropTypes.bool,
  deviceStreamsElevatURL: PropTypes.string,
  deviceStreamsError: PropTypes.string,
  bridgeElevatURL: PropTypes.string,
  bridgeKey: PropTypes.string,
  bridgeKeyUpdatedTime: PropTypes.string,
  identityAssetIsOnline: PropTypes.bool,
  identityConnectionStateUpdatedTime: PropTypes.string,
  identityLastActivityTime: PropTypes.string,
  identity2AssetIsOnline: PropTypes.bool,
  identity2ConnectionStateUpdatedTime: PropTypes.string,
  identity2LastActivityTime: PropTypes.string,
  isRestrictedAssetForOtherThings: PropTypes.bool,
  otaHiddenForEdge: PropTypes.bool,
  toggleRawAPIModal: PropTypes.func,
  toggleUploadCustomerOTAModal: PropTypes.func,
  toggleRunCustomerOTAModal: PropTypes.func,
  toggleDeleteCustomerOTAModal: PropTypes.func,
  customerOTAlist: PropTypes.arrayOf(
    PropTypes.shape({
      filename: PropTypes.string, // just the filename
      displayName: PropTypes.string, // the filename but maybe truncated for display
      name: PropTypes.string, // blob storage name
      type: PropTypes.string, // just the prefix like "hfx_flash"
      typeName: PropTypes.string, // name found from looking up in our types table
      properties: PropTypes.object, // from blob storage
      createdOn: PropTypes.string, // "x ago" string from properties.createdOn time
    })
  ),
};
